/**
 * Free-tier dashboard-count meter + upgrade row shown in the Dashboard
 * switcher popover (S16) — split out of `DashboardSwitcher.tsx` to keep it
 * under the repo's ~200-line guideline. Display-only; the free cap is
 * enforced in the main-process repository. The meter fills proportionally
 * BELOW the cap too (e.g. 1 of 2 renders half-filled) so hitting the limit
 * is never a surprise, and the count is spelled out as "N / LIMIT dashboards"
 * to avoid the ambiguity of a bare "limit reached (N)".
 */
import { Progress } from '@/components/ui/progress';
import { FREE_DASHBOARD_LIMIT } from '../types/dashboardTypes';

type DashboardFreeMeterProps = {
  count: number;
  onUpgradeClick: () => void;
};

export function DashboardFreeMeter({ count, onUpgradeClick }: DashboardFreeMeterProps) {
  return (
    <div
      className="mt-2 flex flex-col gap-1.5 rounded-md bg-muted/50 px-2 py-1.5"
      data-testid="dashboard-free-meter"
    >
      <div className="flex items-center gap-2">
        <Progress
          value={Math.min(100, (count / FREE_DASHBOARD_LIMIT) * 100)}
          className="h-1.5 flex-1"
        />
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {count} / {FREE_DASHBOARD_LIMIT} dashboards
        </span>
      </div>
      <button
        type="button"
        onClick={onUpgradeClick}
        className="text-left text-xs text-muted-foreground hover:text-foreground"
        data-testid="dashboard-free-meter-upgrade"
      >
        Unlimited dashboards with{' '}
        <span className="font-medium text-primary underline underline-offset-2">Pro — upgrade</span>
      </button>
    </div>
  );
}
